// The chat line as DATA: the text, where the caret is in it, and what each edit does to both.
//
// It lives apart from the widget that draws it (gui/typing.ts) and from the manager that feeds it
// keystrokes (gui_manager) because this is the part with all the edge cases -- a caret in the
// middle of the line, word-wise deletion, pasted text that has to fit under the length cap, a
// history walk that must not eat the half-written line it was started from -- and all of them can
// then be tested without a renderer (gui/__tests__/chat_input_model.test.ts).
//
// Every function here is PURE: it returns a new state and never touches the one it was given.

export const MAX_CHAT_LENGTH = 150

export interface ChatInputState {
    value: string
    // Caret offset in characters, 0..value.length. The caret sits BEFORE the character at this
    // index, so `value.length` is "at the end".
    caret: number
}

export const EMPTY_INPUT: ChatInputState = { value: "", caret: 0 }

export type EditScope = "char" | "word" | "line"
export type CaretTarget = "left" | "right" | "home" | "end"

const clampCaret = (value: string, caret: number): number => Math.max(0, Math.min(value.length, caret))

export const inputOf = (value: string, caret?: number): ChatInputState => ({
    value,
    caret: clampCaret(value, caret === undefined ? value.length : caret),
})

// Pasted text arrives from the OS and can be anything: several lines, tabs, a stray control
// character from a terminal copy. Chat is one line, so newlines and tabs become single spaces
// rather than being dropped (dropping them glues the last word of one line to the first of the
// next) and other control characters go.
const CONTROL_CHARS = new RegExp("[\\u0000-\\u001f\\u007f]", "g")
const LINE_BREAKS = new RegExp("[\\r\\n\\t\\v\\f]+", "g")

export const sanitizePasted = (text: string): string => text.replace(LINE_BREAKS, " ").replace(CONTROL_CHARS, "")

export const insertText = (state: ChatInputState, text: string, maxLength = MAX_CHAT_LENGTH): ChatInputState => {
    const clean = sanitizePasted(text)
    if (!clean) {
        return state
    }
    // Truncate the INSERTION rather than the result, so pasting a wall of text into the middle of
    // a line keeps what is already there instead of cutting the tail off it.
    const room = Math.max(0, maxLength - state.value.length)
    const fitted = clean.slice(0, room)
    if (!fitted) {
        return state
    }
    return {
        value: state.value.slice(0, state.caret) + fitted + state.value.slice(state.caret),
        caret: state.caret + fitted.length,
    }
}

// Start of the word before `index`: skip any run of spaces, then the run of non-spaces. Matches
// what Alt/Ctrl+Backspace does in a terminal and in a browser text field.
const wordStart = (value: string, index: number): number => {
    let i = index
    while (i > 0 && value[i - 1] === " ") {
        i--
    }
    while (i > 0 && value[i - 1] !== " ") {
        i--
    }
    return i
}

const wordEnd = (value: string, index: number): number => {
    let i = index
    while (i < value.length && value[i] === " ") {
        i++
    }
    while (i < value.length && value[i] !== " ") {
        i++
    }
    return i
}

export const deleteBackward = (state: ChatInputState, scope: EditScope = "char"): ChatInputState => {
    if (state.caret === 0) {
        return state
    }
    const from = scope === "line" ? 0 : scope === "word" ? wordStart(state.value, state.caret) : state.caret - 1
    return {
        value: state.value.slice(0, from) + state.value.slice(state.caret),
        caret: from,
    }
}

// Forward word-delete also swallows the gap it leaves behind -- deleting "the" out of "feed the
// fire" has to give back "feed fire", not "feed  fire".
const wordEndWithGap = (value: string, index: number): number => {
    let i = wordEnd(value, index)
    while (i < value.length && value[i] === " ") {
        i++
    }
    return i
}

export const deleteForward = (state: ChatInputState, scope: EditScope = "char"): ChatInputState => {
    if (state.caret >= state.value.length) {
        return state
    }
    const to =
        scope === "line"
            ? state.value.length
            : scope === "word"
            ? wordEndWithGap(state.value, state.caret)
            : state.caret + 1
    return {
        value: state.value.slice(0, state.caret) + state.value.slice(to),
        caret: state.caret,
    }
}

export const moveCaret = (state: ChatInputState, target: CaretTarget, byWord = false): ChatInputState => {
    switch (target) {
        case "home":
            return { ...state, caret: 0 }
        case "end":
            return { ...state, caret: state.value.length }
        case "left":
            return { ...state, caret: byWord ? wordStart(state.value, state.caret) : Math.max(0, state.caret - 1) }
        case "right":
            return {
                ...state,
                caret: byWord ? wordEnd(state.value, state.caret) : Math.min(state.value.length, state.caret + 1),
            }
    }
}

// --- sent-line history (the up/down arrows) ------------------------------------------------

export interface ChatHistoryWalk {
    entries: string[]
    // -1 == "not walking": the player is editing a fresh line.
    index: number
    // The half-written line the walk was started from, handed back when they walk off the recent
    // end of the history again. Without it, pressing Up to check what you said last THREW AWAY
    // what you were in the middle of writing.
    draft: string
}

export const EMPTY_WALK: ChatHistoryWalk = { entries: [], index: -1, draft: "" }

export interface WalkResult {
    walk: ChatHistoryWalk
    input: ChatInputState
}

export const historyBack = (walk: ChatHistoryWalk, current: ChatInputState): WalkResult => {
    if (walk.entries.length === 0) {
        return { walk, input: current }
    }
    if (walk.index === -1) {
        const index = walk.entries.length - 1
        return { walk: { ...walk, index, draft: current.value }, input: inputOf(walk.entries[index]) }
    }
    const index = Math.max(0, walk.index - 1)
    return { walk: { ...walk, index }, input: inputOf(walk.entries[index]) }
}

export const historyForward = (walk: ChatHistoryWalk, current: ChatInputState): WalkResult => {
    if (walk.index === -1) {
        return { walk, input: current }
    }
    if (walk.index >= walk.entries.length - 1) {
        // Off the recent end: back to whatever was being written when the walk started.
        return { walk: { ...walk, index: -1, draft: "" }, input: inputOf(walk.draft) }
    }
    const index = walk.index + 1
    return { walk: { ...walk, index }, input: inputOf(walk.entries[index]) }
}

export const HISTORY_LIMIT = 50

// Records a sent line. Consecutive duplicates collapse, and the walk is reset so the next Up
// starts from the newest line rather than wherever the last walk left off.
export const rememberSent = (walk: ChatHistoryWalk, message: string): ChatHistoryWalk => {
    if (!message) {
        return { ...walk, index: -1, draft: "" }
    }
    const last = walk.entries[walk.entries.length - 1]
    const entries = last === message ? walk.entries : [...walk.entries, message].slice(-HISTORY_LIMIT)
    return { entries, index: -1, draft: "" }
}
